<template>
    <Modal
        title="Client Details"
        :model-value="isModalVisible"
        data-testid="client-details__modal"
        @onCancel="closeClientModal"
    >
        <hox-loading-layer v-if="isLoadingCreativeX || isLoadingCreativeXSecrets" :is-full-screen="false" />
        <Form
            ref="form"
            class="edit-client"
            :show-message="false"
            :model="form"
            :label-width="120"
            :rules="validationRules"
            @submit.prevent
        >
            <FormItem label="Client name" prop="name">
                <Input
                    ref="name"
                    v-model="form.name"
                    auto-complete="off"
                    data-testid="edit-client__name"
                    @keydown.enter="confirmAddClient"
                />
            </FormItem>

            <template v-if="userIsSuperAdmin && form.creativeXPlatformSettings?.enabled">
                <FormItem label="CreativeX Secret" prop="creativeXPlatformSettings.secretId">
                    <Select
                        v-model="form.creativeXPlatformSettings.secretId"
                        data-testid="edit-client__secret-id"
                        @keydown.enter="confirmAddClient"
                    >
                        <Option v-for="option in creativeXSecrets" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                    <CreativeXError v-if="creativeXSecretsHasError" @refetch="fetchCreativeXSecrets" />
                </FormItem>

                <FormItem
                    v-if="form.creativeXPlatformSettings.secretId"
                    label="Brand ID"
                    prop="creativeXPlatformSettings.brandId"
                >
                    <Select
                        v-model="form.creativeXPlatformSettings.brandId"
                        data-testid="edit-client__brand-id"
                        @keydown.enter="confirmAddClient"
                    >
                        <Option v-for="option in creativeXOptions?.brands" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                    <CreativeXError v-if="creativeXHasError" @refetch="fetchCreativeXDimensions" />
                </FormItem>
            </template>

            <template v-if="isEditClient">
                <FormItem v-if="userIsSuperAdmin" label="Facebook">
                    <Input
                        v-model="form.fbBusinessId"
                        placeholder="Business ID"
                        auto-complete="off"
                        data-testid="edit-client__fb-business-id"
                        @keydown.enter="confirmAddClient"
                    />
                    <Input
                        v-model="form.fbAdAccountId"
                        placeholder="Ad Account ID"
                        auto-complete="off"
                        data-testid="edit-client__fb-account-id"
                        @keydown.enter="confirmAddClient"
                    />
                </FormItem>

                <FormItem label="Logo">
                    <ImageUpload
                        :model-value="form.logo"
                        :config="form.uploadConfig as MastheadUploadConfig"
                        data-testid="edit-client__logo"
                        @imageUploading="disableConfirm = true"
                        @imageUploaded="onImageUploaded"
                        @imageUploadFailed="disableConfirm = false"
                    />
                </FormItem>

                <FormItem label="Distribution Channel">
                    <Select
                        v-model="form.distributionChannel"
                        data-testid="edit-client__distribution-channel"
                        @keydown.enter="confirmAddClient"
                    >
                        <Option
                            v-for="option in distributionChannelOptions"
                            :key="option.value"
                            :value="option.value"
                            :data-testid="`edit-client__distribution-channel-option-${option.value}`"
                        >
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>

                <FormItem v-if="userCanEnableImageProcessing">
                    <Checkbox v-model="form.imageProcessingEnabled" data-testid="edit-client__enable-image-processing">
                        Enable Generative AI features
                    </Checkbox>
                </FormItem>

                <FormItem v-if="userIsSuperAdmin && form.creativeXPlatformSettings">
                    <Checkbox
                        v-model="form.creativeXPlatformSettings.enabled"
                        data-testid="edit-client__enable-creative-x"
                    >
                        Enable CreativeX
                    </Checkbox>
                </FormItem>

                <FormItem
                    v-if="form.creativeXPlatformSettings?.enabled"
                    label="Passing threshold"
                    prop="creativeXPlatformSettings.threshold"
                >
                    <InputNumber
                        v-model="form.creativeXPlatformSettings.threshold"
                        class="edit-client__threshold"
                        :min="1"
                        :max="99"
                        placeholder="Enter a value between 1 and 99"
                        data-testid="edit-client__threshold"
                        :parser="(value: string) => value.replace('.', '')"
                    />
                </FormItem>
            </template>
        </Form>

        <template #footer>
            <span class="dialog-footer">
                <Button
                    v-if="isEditClient && userCanRemoveClient"
                    class="edit-client-button__delete"
                    icon="ios-trash-outline"
                    data-testid="edit-client-button__delete"
                    type="error"
                    ghost
                    @click="deleteClient(form)"
                >
                    Delete Client
                </Button>
                <Button
                    class="edit-client-button__cancel"
                    data-testid="edit-client-button__cancel"
                    @click="closeClientModal"
                >
                    Cancel
                </Button>
                <Button
                    class="edit-client-button__confirm"
                    data-testid="edit-client-button__confirm"
                    type="primary"
                    :disabled="disableConfirm || isLoading"
                    @click="confirmAddClient"
                >
                    Confirm
                </Button>
            </span>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import { CreativeXScore } from "shared-utils/enums/creativex";
import { USER_ACTIONS } from "shared-utils/enums/user";

import bus from "@/bus";

import ImageUpload from "@/components/ImageUpload.vue";
import CreativeXError from "@/components/CreativeXError.vue";

import addClientMutation from "@/apollo/mutations/AddClient.gql";
import removeClientMutation from "@/apollo/mutations/RemoveClient.gql";
import updateClientMutation from "@/apollo/mutations/UpdateClient.gql";
import clientsQuery from "@/apollo/queries/Clients.gql";

import { AuthGetters } from "@/store/modules/auth";

import type { Client, DistributionChannel, Err, IViewForm, MastheadUploadConfig } from "@/types/types";
import { getCreativeXDimensions, getCreativeXSecrets } from "@/utils/CreativeIntelligenceApi/creativex";
import formatCreativeXDimensions from "@/utils/CreativeX/formatCreativeXDimensions";
import type { CreativeXSecret } from "@/types/preflight/creativeX";
import GA4 from "@/enums/ga4Events";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { userCan } from "@/services/Auth";

import { DistributionChannelLabel } from "@/enums/platforms";
import { DISTRIBUTION_CHANNEL } from "shared-utils/enums/client";

export type ClientFormData = {
    _id: string;
    id: string;
    name: string;
    logo: string;
    content?: string;
    distributionChannel?: DistributionChannel | null;
    creativeXPlatformSettings?: {
        enabled: boolean;
        brandId: string | null;
        threshold: number | null;
        secretId: string | null;
    } | null;
    imageProcessingEnabled?: boolean;
    fbAdAccountId?: string;
    fbBusinessId?: string;
    uploadConfig?: MastheadUploadConfig;
};

export default defineComponent({
    name: "ClientModal",
    components: {
        ImageUpload,
        CreativeXError
    },
    props: {
        isModalVisible: {
            type: Boolean,
            required: true
        },
        client: {
            type: [Object, null] as PropType<Client | null>,
            required: true
        },
        sortClientsBy: {
            type: String,
            required: true
        }
    },

    emits: ["close"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            form: this.resetForm?.() as ClientFormData,
            validationRules: {
                "creativeXPlatformSettings.brandId": [
                    { required: true, message: "Please select a brand", trigger: "change" }
                ],
                "creativeXPlatformSettings.threshold": [
                    {
                        type: "number",
                        required: true,
                        min: 0,
                        max: 100,
                        message: "Threshold must be between 0 and 100",
                        trigger: "blur"
                    }
                ],
                "creativeXPlatformSettings.secretId": [
                    { required: true, message: "Please select a secret", trigger: "change" }
                ],
                name: [{ required: true, message: "Client name is a required field", whitespace: true }]
            },
            isLoading: false,
            isLoadingCreativeX: false,
            isLoadingCreativeXSecrets: false,
            disableConfirm: false,
            isPopulatingForm: false,
            creativeXOptions: null as ReturnType<typeof formatCreativeXDimensions>,
            creativeXSecrets: [] as CreativeXSecret[],
            creativeXHasError: false,
            creativeXSecretsHasError: false,
            distributionChannelOptions: Object.values(DISTRIBUTION_CHANNEL).map(value => ({
                value,
                label: DistributionChannelLabel[value]
            }))
        };
    },

    computed: {
        isEditClient() {
            return this.form.id;
        },

        userIsSuperAdmin() {
            return this.$store.getters[AuthGetters.isSuperAdmin];
        },

        savedDistributionChannel() {
            return this.client?.distributionChannel ?? DISTRIBUTION_CHANNEL.NONE;
        },

        hasDistributionChannelChanged() {
            return this.isEditClient && this.form.distributionChannel !== this.savedDistributionChannel;
        },

        enabledFeatures() {
            const enabledFeatures = [];

            if (this.form.imageProcessingEnabled) {
                enabledFeatures.push("Generative AI features");
            }

            if (this.form.distributionChannel) {
                enabledFeatures.push("Distribution Channel");
            }

            if (this.form.creativeXPlatformSettings?.enabled) {
                enabledFeatures.push("CreativeX");
            }

            return enabledFeatures;
        },

        userCanEnableImageProcessing() {
            return userCan(USER_ACTIONS.canEnableImageProcessing);
        },

        userCanRemoveClient() {
            return userCan(USER_ACTIONS.canRemoveClient, { clientId: this.form.id });
        }
    },

    watch: {
        isModalVisible: {
            handler(newVal: boolean) {
                if (!newVal) {
                    return;
                }

                this.populateForm();

                if (this.form.creativeXPlatformSettings?.enabled) {
                    this.fetchCreativeXSecrets();

                    // Only fetch dimensions if a secret is selected
                    if (this.form.creativeXPlatformSettings.secretId) {
                        this.fetchCreativeXDimensions();
                    }
                }
            },
            immediate: true
        },
        "form.creativeXPlatformSettings.enabled"(newVal: boolean) {
            if (!newVal) {
                return;
            }

            this.fetchCreativeXSecrets();
        },
        "form.creativeXPlatformSettings.secretId"(newVal: string | null) {
            // Don't clear brandId when populating form with existing data
            if (!this.isPopulatingForm) {
                if (this.form.creativeXPlatformSettings) {
                    this.form.creativeXPlatformSettings.brandId = null;
                }

                this.creativeXOptions = null;
            }

            if (!newVal) {
                return;
            }

            this.fetchCreativeXDimensions();
        },
        client: {
            handler() {
                this.populateForm();
            },
            immediate: true
        }
    },

    methods: {
        populateForm() {
            if (this.client) {
                this.isPopulatingForm = true;
                this.form._id = this.client._id;
                this.form.id = this.client.id;
                this.form.name = this.client.name;
                this.form.fbBusinessId = this.client.fbAdsManagerConfig?.businessId;
                this.form.fbAdAccountId = this.client.fbAdsManagerConfig?.adAccountId;
                this.form.uploadConfig = this.client.mastheadUploadConfig;
                this.form.logo = this.client.logoUrl ?? "";
                this.form.content = "";
                this.form.imageProcessingEnabled = this.client.imageProcessingEnabled;
                this.form.distributionChannel = this.client.distributionChannel ?? DISTRIBUTION_CHANNEL.NONE;
                this.form.creativeXPlatformSettings = {
                    brandId: this.client.creativeXPlatformSettings?.brandId?.toString() || null,
                    enabled: this.client.creativeXPlatformSettings?.enabled || false,
                    threshold: Math.round(
                        (this.client.creativeXPlatformSettings?.threshold ?? CreativeXScore.THRESHOLD) * 100
                    ),
                    secretId: this.client.creativeXPlatformSettings?.secretId || null
                };

                this.$nextTick(() => {
                    this.isPopulatingForm = false;
                });
            }
        },

        confirmDistributionChannelChange() {
            const from = DistributionChannelLabel[this.savedDistributionChannel];
            const to = DistributionChannelLabel[this.form.distributionChannel ?? DISTRIBUTION_CHANNEL.NONE];

            this.$Modal.confirm({
                title: "Change distribution channel?",
                content:
                    `You are changing the distribution channel of "${this.form.name}" from ${from} to ${to}. ` +
                    "Connect accounts, publishing rules or OMS settings already configured for this client " +
                    "may stop working.",
                okText: "Change",
                onOk: () => this.patchClient()
            });
        },

        closeClientModal() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                featureContext: GA4.FEATURE_CONTEXTS.EDIT_CLIENT
            });

            this.form = this.resetForm();
            this.$emit("close");
        },

        confirmAddClient() {
            (this.$refs.form as IViewForm).validate((valid: boolean) => {
                if (!valid) {
                    this.$snackbar.error("Please fill in the required fields");

                    return;
                }

                if (this.enabledFeatures?.length) {
                    this.ga4.trackFeatureEvent({
                        featureEvent: GA4.EVENT_TYPES.FEATURE_SETTINGS,
                        featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                        featureContext: GA4.FEATURE_CONTEXTS.EDIT_CLIENT,
                        freeformValues: this.enabledFeatures
                    });
                }

                if (!this.isEditClient) {
                    this.addClient();
                } else {
                    if (
                        (this.form.fbAdAccountId && !this.form.fbBusinessId) ||
                        (!this.form.fbAdAccountId && this.form.fbBusinessId)
                    ) {
                        this.$snackbar.error(
                            "Please ensure both Facebook ID fields are filled out: Business ID and Ad Account ID."
                        );

                        return;
                    }

                    if (this.hasDistributionChannelChanged) {
                        this.confirmDistributionChannelChange();

                        return;
                    }

                    this.patchClient();
                }
            });
        },

        deleteClient(form: ClientFormData) {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                featureContext: GA4.FEATURE_CONTEXTS.DELETE_CLIENT
            });

            this.$Modal.confirm({
                title: `Delete client ${form.name}?`,
                content: `Deleting the client "${form.name}" is permanent and can not be undeleted.`,
                okText: "Delete",
                onOk: async () => {
                    this.isLoading = true;
                    bus.$emit("percentLoadEvent", 0);
                    try {
                        await this.$apollo.mutate({
                            mutation: removeClientMutation,
                            variables: {
                                clientId: form.id
                            },
                            refetchQueries: [
                                {
                                    query: clientsQuery,
                                    variables: {
                                        orderBy: this.sortClientsBy,
                                        wppProjectId: this.$store.state.auth?.wppOsContext?.project?.id
                                    }
                                }
                            ]
                        });

                        bus.$emit("percentLoadEvent", 100);
                        this.$snackbar.success("Client deleted");
                        this.ga4.trackFeatureEvent({
                            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                            featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                            featureContext: GA4.FEATURE_CONTEXTS.DELETE_CLIENT
                        });

                        this.closeClientModal();
                    } catch (err) {
                        this.$snackbar.error((err as Err).message);
                    }

                    this.isLoading = false;
                },
                onCancel: () => {
                    this.ga4.trackFeatureEvent({
                        featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                        featureCategory: GA4.FEATURE_CATEGORIES.CLIENT,
                        featureContext: GA4.FEATURE_CONTEXTS.DELETE_CLIENT
                    });
                }
            });
        },

        addClient() {
            if (this.isLoading) {
                return;
            }

            bus.$emit("percentLoadEvent", 0);
            this.isLoading = true;

            this.$apollo
                .mutate({
                    mutation: addClientMutation,
                    variables: {
                        name: this.form.name
                    },
                    refetchQueries: [
                        {
                            query: clientsQuery,
                            variables: {
                                orderBy: this.sortClientsBy,
                                wppProjectId: this.$store.state.auth?.wppOsContext?.project?.id
                            }
                        }
                    ]
                })
                .then(() => {
                    this.$snackbar.success(`The client "${this.form.name}" has been added.`);
                    this.closeClientModal();
                })
                .catch(err => {
                    this.$snackbar.error(err.message);
                })
                .finally(() => {
                    bus.$emit("percentLoadEvent", 100);
                    this.isLoading = false;
                });
        },

        patchClient() {
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;
            bus.$emit("percentLoadEvent", 0);

            const fbAdsManagerConfig = this.form.fbBusinessId &&
                this.form.fbAdAccountId && {
                    adAccountId: this.form.fbAdAccountId,
                    businessId: this.form.fbBusinessId
                };

            this.$apollo
                .mutate({
                    mutation: updateClientMutation,
                    variables: {
                        clientId: this.form._id,
                        content: this.form.content,
                        name: this.form.name,
                        imageProcessingEnabled: this.form.imageProcessingEnabled,
                        distributionChannel: this.form.distributionChannel,
                        creativeXPlatformSettings: {
                            enabled: this.form.creativeXPlatformSettings?.enabled,
                            brandId: this.form.creativeXPlatformSettings?.enabled
                                ? Number(this.form.creativeXPlatformSettings.brandId)
                                : null,
                            threshold: this.form.creativeXPlatformSettings?.enabled
                                ? this.form.creativeXPlatformSettings.threshold! / 100
                                : null,
                            secretId: this.form.creativeXPlatformSettings?.secretId
                        },
                        // if not provided it should unset facebook for this client
                        fbAdsManagerConfig: fbAdsManagerConfig || null
                    },
                    refetchQueries: [
                        {
                            query: clientsQuery,
                            variables: {
                                orderBy: this.sortClientsBy,
                                wppProjectId: this.$store.state.auth?.wppOsContext?.project?.id
                            }
                        }
                    ]
                })
                .then(() => {
                    this.$snackbar.success(`The client "${this.form.name}" has been updated`);
                    this.closeClientModal();
                })
                .catch(err => {
                    this.$snackbar.unauthorized(err);
                })
                .finally(() => {
                    bus.$emit("percentLoadEvent", 100);
                    this.isLoading = false;
                });
        },

        resetForm() {
            return {
                _id: "",
                id: "",
                name: "",
                uploadConfig: undefined,
                logo: "",
                content: "",
                imageProcessingEnabled: undefined,
                distributionChannel: null,
                creativeXPlatformSettings: {
                    enabled: false,
                    brandId: null,
                    threshold: null,
                    secretId: null
                },
                fbBusinessId: "",
                fbAdAccountId: ""
            };
        },

        onImageUploaded(event: string) {
            this.disableConfirm = false;
            this.form.content = event;
        },

        async fetchCreativeXDimensions() {
            if (this.isLoadingCreativeX) {
                return;
            }

            const secretId = this.form.creativeXPlatformSettings?.secretId;
            if (!secretId) {
                return;
            }

            this.creativeXHasError = false;
            this.isLoadingCreativeX = true;
            try {
                const { data } = await getCreativeXDimensions(secretId);

                this.creativeXOptions = formatCreativeXDimensions(data);
            } catch {
                this.creativeXHasError = true;
                this.$snackbar.error("Failed to fetch CreativeX dimensions");
            } finally {
                this.isLoadingCreativeX = false;
            }
        },

        async fetchCreativeXSecrets() {
            if (this.isLoadingCreativeXSecrets) {
                return;
            }

            this.creativeXSecretsHasError = false;
            this.isLoadingCreativeXSecrets = true;

            try {
                const { data } = await getCreativeXSecrets();

                this.creativeXSecrets = data;
            } catch {
                this.creativeXSecretsHasError = true;
                this.$snackbar.error("Failed to fetch CreativeX secrets");
            } finally {
                this.isLoadingCreativeXSecrets = false;
            }
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.edit-client-button__delete {
    float: left;
}

.edit-client__threshold {
    width: 100%;
}

.warning-text {
    color: var(--wpp-warning-color-500);
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 0;
}

.error-text {
    color: var(--wpp-danger-color-500);
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 0;
}
</style>
